import { ref, computed } from 'vue'

// Language mapping for browser language detection
const languageMap = {
  'zh': 'zh-CN',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-CN',
  'zh-HK': 'zh-CN',
  'en': 'en-US',
  'en-US': 'en-US',
  'en-GB': 'en-US',
  'ja': 'ja-JP',
  'ja-JP': 'ja-JP',
  'es': 'es-ES',
  'es-ES': 'es-ES',
  'es-MX': 'es-ES',
  'fr': 'fr-FR',
  'fr-FR': 'fr-FR',
  'fr-CA': 'fr-FR',
  'de': 'de-DE',
  'de-DE': 'de-DE',
  'de-AT': 'de-DE',
  'ko': 'ko-KR',
  'ko-KR': 'ko-KR',
  'pt': 'pt-BR',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-BR',
  'ru': 'ru-RU',
  'ru-RU': 'ru-RU',
  'it': 'it-IT',
  'it-IT': 'it-IT'
}

// Detect browser language and map to supported language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en-US'
  const shortLang = browserLang.split('-')[0]
  
  // Try exact match first, then language family, then default
  return languageMap[browserLang] || languageMap[shortLang] || 'en-US'
}

const currentLanguage = ref(detectBrowserLanguage())

const messages = {
  'zh-CN': {
    ui: {
      date: '日期',
      selectEffect: '选择特效',
      noEffect: '无特效',
      selectLanguage: '选择语言',
      rain: '雨',
      snow: '雪', 
      stars: '星空'
    },
    music: {
      fire: '火焰',
      forest: '森林',
      water: '流水',
      ocean: '海洋之声',
      faraway: '远方',
      cafe: '咖啡厅',
      storm: '风暴',
      woodenFish: '木鱼',
      naturalSounds: '自然音效',
      ambientMusic: '环境音乐',
      lightMusic: '轻音乐',
      meditationMusic: '冥想音乐',
      unknown: '未知'
    },
    settings: {
      title: '设置',
      languageSettings: '语言设置',
      displaySettings: '显示设置',
      musicSettings: '音乐设置',
      hideBackground: '隐藏背景图片',
      volumeControl: '音量控制',
      uploadMusic: '上传音乐',
      uploadedMusic: '已上传音乐',
      setVolumeTo: '设置音量为',
      delete: '删除',
      low: '低',
      medium: '中',
      high: '高',
      selectAudioFile: '请选择音频文件',
      userUploaded: '用户上传'
    },
    languages: {
      'zh-CN': '中文',
      'en-US': 'English',
      'ja-JP': '日本語',
      'es-ES': 'Español',
      'fr-FR': 'Français',
      'de-DE': 'Deutsch',
      'ko-KR': '한국어',
      'pt-BR': 'Português',
      'ru-RU': 'Русский',
      'it-IT': 'Italiano'
    }
  },
  'en-US': {
    ui: {
      date: 'Date',
      selectEffect: 'Select Effect',
      noEffect: 'No Effect',
      selectLanguage: 'Select Language',
      rain: 'Rain',
      snow: 'Snow',
      stars: 'Stars'
    },
    music: {
      fire: 'Fire',
      forest: 'Forest',
      water: 'Water',
      ocean: 'Ocean Sounds',
      faraway: 'Faraway',
      cafe: 'Cafe',
      storm: 'Storm',
      woodenFish: 'Wooden Fish',
      naturalSounds: 'Natural Sounds',
      ambientMusic: 'Ambient Music',
      lightMusic: 'Light Music',
      meditationMusic: 'Meditation Music',
      unknown: 'Unknown'
    },
    settings: {
      title: 'Settings',
      languageSettings: 'Language Settings',
      displaySettings: 'Display Settings',
      musicSettings: 'Music Settings',
      hideBackground: 'Hide Background Image',
      volumeControl: 'Volume Control',
      uploadMusic: 'Upload Music',
      uploadedMusic: 'Uploaded Music',
      setVolumeTo: 'Set volume to',
      delete: 'Delete',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      selectAudioFile: 'Please select an audio file',
      userUploaded: 'User Uploaded'
    },
    languages: {
      'zh-CN': '中文',
      'en-US': 'English',
      'ja-JP': '日本語',
      'es-ES': 'Español',
      'fr-FR': 'Français',
      'de-DE': 'Deutsch',
      'ko-KR': '한국어',
      'pt-BR': 'Português',
      'ru-RU': 'Русский',
      'it-IT': 'Italiano'
    }
  },
  'ja-JP': {
    ui: {
      date: '日付',
      selectEffect: 'エフェクト選択',
      noEffect: 'エフェクトなし',
      selectLanguage: '言語選択',
      rain: '雨',
      snow: '雪',
      stars: '星空'
    },
    music: {
      fire: '炎',
      forest: '森',
      water: '水流',
      ocean: '海の音',
      faraway: '遠方',
      cafe: 'カフェ',
      storm: '嵐',
      woodenFish: '木魚',
      naturalSounds: '自然音',
      ambientMusic: 'アンビエント音楽',
      lightMusic: 'ライト音楽',
      meditationMusic: '瞑想音楽',
      unknown: '不明'
    },
    settings: {
      title: '設定',
      languageSettings: '言語設定',
      displaySettings: '表示設定',
      musicSettings: '音楽設定',
      hideBackground: '背景画像を隠す',
      volumeControl: '音量調整',
      uploadMusic: '音楽アップロード',
      uploadedMusic: 'アップロード音楽',
      setVolumeTo: '音量を設定：',
      delete: '削除',
      low: '低',
      medium: '中',
      high: '高',
      selectAudioFile: 'オーディオファイルを選択してください',
      userUploaded: 'ユーザーアップロード'
    },
    languages: {
      'zh-CN': '中国語',
      'en-US': '英語',
      'ja-JP': '日本語',
      'es-ES': 'スペイン語',
      'fr-FR': 'フランス語',
      'de-DE': 'ドイツ語',
      'ko-KR': '韓国語',
      'pt-BR': 'ポルトガル語',
      'ru-RU': 'ロシア語',
      'it-IT': 'イタリア語'
    }
  },
  'es-ES': {
    ui: {
      date: 'Fecha',
      selectEffect: 'Seleccionar Efecto',
      noEffect: 'Sin Efecto',
      selectLanguage: 'Seleccionar Idioma',
      rain: 'Lluvia',
      snow: 'Nieve',
      stars: 'Estrellas'
    },
    music: {
      fire: 'Fuego',
      forest: 'Bosque',
      water: 'Agua',
      ocean: 'Sonidos del Océano',
      faraway: 'Lejano',
      cafe: 'Cafetería',
      storm: 'Tormenta',
      woodenFish: 'Pez de Madera',
      naturalSounds: 'Sonidos Naturales',
      ambientMusic: 'Música Ambiental',
      lightMusic: 'Música Ligera',
      meditationMusic: 'Música de Meditación',
      unknown: 'Desconocido'
    },
    settings: {
      title: 'Configuración',
      languageSettings: 'Configuración de Idioma',
      displaySettings: 'Configuración de Pantalla',
      musicSettings: 'Configuración de Música',
      hideBackground: 'Ocultar Imagen de Fondo',
      volumeControl: 'Control de Volumen',
      uploadMusic: 'Subir Música',
      uploadedMusic: 'Música Subida',
      setVolumeTo: 'Ajustar volumen a',
      delete: 'Eliminar',
      low: 'Bajo',
      medium: 'Medio',
      high: 'Alto',
      selectAudioFile: 'Por favor seleccione un archivo de audio',
      userUploaded: 'Subido por Usuario'
    },
    languages: {
      'zh-CN': 'Chino',
      'en-US': 'Inglés',
      'ja-JP': 'Japonés',
      'es-ES': 'Español',
      'fr-FR': 'Francés',
      'de-DE': 'Alemán',
      'ko-KR': 'Coreano',
      'pt-BR': 'Portugués',
      'ru-RU': 'Ruso',
      'it-IT': 'Italiano'
    }
  },
  'fr-FR': {
    ui: {
      date: 'Date',
      selectEffect: 'Sélectionner Effet',
      noEffect: 'Aucun Effet',
      selectLanguage: 'Sélectionner Langue',
      rain: 'Pluie',
      snow: 'Neige',
      stars: 'Étoiles'
    },
    music: {
      fire: 'Feu',
      forest: 'Forêt',
      water: 'Eau',
      ocean: 'Sons de l\'Océan',
      faraway: 'Lointain',
      cafe: 'Café',
      storm: 'Orage',
      woodenFish: 'Poisson en Bois',
      naturalSounds: 'Sons Naturels',
      ambientMusic: 'Musique Ambiante',
      lightMusic: 'Musique Légère',
      meditationMusic: 'Musique de Méditation',
      unknown: 'Inconnu'
    },
    settings: {
      title: 'Paramètres',
      languageSettings: 'Paramètres de Langue',
      displaySettings: 'Paramètres d\'Affichage',
      musicSettings: 'Paramètres Musicaux',
      hideBackground: 'Masquer l\'Image de Fond',
      volumeControl: 'Contrôle du Volume',
      uploadMusic: 'Télécharger Musique',
      uploadedMusic: 'Musique Téléchargée',
      setVolumeTo: 'Régler le volume à',
      delete: 'Supprimer',
      low: 'Bas',
      medium: 'Moyen',
      high: 'Haut',
      selectAudioFile: 'Veuillez sélectionner un fichier audio',
      userUploaded: 'Téléchargé par l\'utilisateur'
    },
    languages: {
      'zh-CN': 'Chinois',
      'en-US': 'Anglais',
      'ja-JP': 'Japonais',
      'es-ES': 'Espagnol',
      'fr-FR': 'Français',
      'de-DE': 'Allemand',
      'ko-KR': 'Coréen',
      'pt-BR': 'Portugais',
      'ru-RU': 'Russe',
      'it-IT': 'Italien'
    }
  },
  'de-DE': {
    ui: {
      date: 'Datum',
      selectEffect: 'Effekt Wählen',
      noEffect: 'Kein Effekt',
      selectLanguage: 'Sprache Wählen',
      rain: 'Regen',
      snow: 'Schnee',
      stars: 'Sterne'
    },
    music: {
      fire: 'Feuer',
      forest: 'Wald',
      water: 'Wasser',
      ocean: 'Meeresrauschen',
      faraway: 'Fern',
      cafe: 'Café',
      storm: 'Sturm',
      woodenFish: 'Holzfisch',
      naturalSounds: 'Naturgeräusche',
      ambientMusic: 'Ambient-Musik',
      lightMusic: 'Leichte Musik',
      meditationMusic: 'Meditationsmusik',
      unknown: 'Unbekannt'
    },
    settings: {
      title: 'Einstellungen',
      languageSettings: 'Spracheinstellungen',
      displaySettings: 'Anzeigeeinstellungen',
      musicSettings: 'Musikeinstellungen',
      hideBackground: 'Hintergrundbild Ausblenden',
      volumeControl: 'Lautstärkeregelung',
      uploadMusic: 'Musik Hochladen',
      uploadedMusic: 'Hochgeladene Musik',
      setVolumeTo: 'Lautstärke einstellen auf',
      delete: 'Löschen',
      low: 'Niedrig',
      medium: 'Mittel',
      high: 'Hoch',
      selectAudioFile: 'Bitte wählen Sie eine Audiodatei',
      userUploaded: 'Benutzer-Upload'
    },
    languages: {
      'zh-CN': 'Chinesisch',
      'en-US': 'Englisch',
      'ja-JP': 'Japanisch',
      'es-ES': 'Spanisch',
      'fr-FR': 'Französisch',
      'de-DE': 'Deutsch',
      'ko-KR': 'Koreanisch',
      'pt-BR': 'Portugiesisch',
      'ru-RU': 'Russisch',
      'it-IT': 'Italienisch'
    }
  },
  'ko-KR': {
    ui: {
      date: '날짜',
      selectEffect: '효과 선택',
      noEffect: '효과 없음',
      selectLanguage: '언어 선택',
      rain: '비',
      snow: '눈',
      stars: '별'
    },
    music: {
      fire: '불',
      forest: '숲',
      water: '물',
      ocean: '바다 소리',
      faraway: '먼 곳',
      cafe: '카페',
      storm: '폭풍',
      woodenFish: '목어',
      naturalSounds: '자연음',
      ambientMusic: '앰비언트 음악',
      lightMusic: '가벼운 음악',
      meditationMusic: '명상 음악',
      unknown: '알 수 없음'
    },
    settings: {
      title: '설정',
      languageSettings: '언어 설정',
      displaySettings: '디스플레이 설정',
      musicSettings: '음악 설정',
      hideBackground: '배경 이미지 숨기기',
      volumeControl: '볼륨 조절',
      uploadMusic: '음악 업로드',
      uploadedMusic: '업로드된 음악',
      setVolumeTo: '볼륨 설정:',
      delete: '삭제',
      low: '낮음',
      medium: '중간',
      high: '높음',
      selectAudioFile: '오디오 파일을 선택해주세요',
      userUploaded: '사용자 업로드'
    },
    languages: {
      'zh-CN': '중국어',
      'en-US': '영어',
      'ja-JP': '일본어',
      'es-ES': '스페인어',
      'fr-FR': '프랑스어',
      'de-DE': '독일어',
      'ko-KR': '한국어',
      'pt-BR': '포르투갈어',
      'ru-RU': '러시아어',
      'it-IT': '이탈리아어'
    }
  },
  'pt-BR': {
    ui: {
      date: 'Data',
      selectEffect: 'Selecionar Efeito',
      noEffect: 'Sem Efeito',
      selectLanguage: 'Selecionar Idioma',
      rain: 'Chuva',
      snow: 'Neve',
      stars: 'Estrelas'
    },
    music: {
      fire: 'Fogo',
      forest: 'Floresta',
      water: 'Água',
      ocean: 'Sons do Oceano',
      faraway: 'Distante',
      cafe: 'Café',
      storm: 'Tempestade',
      woodenFish: 'Peixe de Madeira',
      naturalSounds: 'Sons Naturais',
      ambientMusic: 'Música Ambiente',
      lightMusic: 'Música Leve',
      meditationMusic: 'Música de Meditação',
      unknown: 'Desconhecido'
    },
    settings: {
      title: 'Configurações',
      languageSettings: 'Configurações de Idioma',
      displaySettings: 'Configurações de Tela',
      musicSettings: 'Configurações de Música',
      hideBackground: 'Ocultar Imagem de Fundo',
      volumeControl: 'Controle de Volume',
      uploadMusic: 'Carregar Música',
      uploadedMusic: 'Música Carregada',
      setVolumeTo: 'Definir volume para',
      delete: 'Excluir',
      low: 'Baixo',
      medium: 'Médio',
      high: 'Alto',
      selectAudioFile: 'Por favor selecione um arquivo de áudio',
      userUploaded: 'Enviado pelo Usuário'
    },
    languages: {
      'zh-CN': 'Chinês',
      'en-US': 'Inglês',
      'ja-JP': 'Japonês',
      'es-ES': 'Espanhol',
      'fr-FR': 'Francês',
      'de-DE': 'Alemão',
      'ko-KR': 'Coreano',
      'pt-BR': 'Português',
      'ru-RU': 'Russo',
      'it-IT': 'Italiano'
    }
  },
  'ru-RU': {
    ui: {
      date: 'Дата',
      selectEffect: 'Выбрать Эффект',
      noEffect: 'Без Эффекта',
      selectLanguage: 'Выбрать Язык',
      rain: 'Дождь',
      snow: 'Снег',
      stars: 'Звёзды'
    },
    music: {
      fire: 'Огонь',
      forest: 'Лес',
      water: 'Вода',
      ocean: 'Звуки Океана',
      faraway: 'Далёкий',
      cafe: 'Кафе',
      storm: 'Буря',
      woodenFish: 'Деревянная Рыба',
      naturalSounds: 'Природные Звуки',
      ambientMusic: 'Эмбиентная Музыка',
      lightMusic: 'Лёгкая Музыка',
      meditationMusic: 'Медитативная Музыка',
      unknown: 'Неизвестно'
    },
    settings: {
      title: 'Настройки',
      languageSettings: 'Настройки Языка',
      displaySettings: 'Настройки Отображения',
      musicSettings: 'Настройки Музыки',
      hideBackground: 'Скрыть Фоновое Изображение',
      volumeControl: 'Управление Громкостью',
      uploadMusic: 'Загрузить Музыку',
      uploadedMusic: 'Загруженная Музыка',
      setVolumeTo: 'Установить громкость',
      delete: 'Удалить',
      low: 'Низкий',
      medium: 'Средний',
      high: 'Высокий',
      selectAudioFile: 'Пожалуйста, выберите аудиофайл',
      userUploaded: 'Загружено пользователем'
    },
    languages: {
      'zh-CN': 'Китайский',
      'en-US': 'Английский',
      'ja-JP': 'Японский',
      'es-ES': 'Испанский',
      'fr-FR': 'Французский',
      'de-DE': 'Немецкий',
      'ko-KR': 'Корейский',
      'pt-BR': 'Португальский',
      'ru-RU': 'Русский',
      'it-IT': 'Итальянский'
    }
  },
  'it-IT': {
    ui: {
      date: 'Data',
      selectEffect: 'Seleziona Effetto',
      noEffect: 'Nessun Effetto',
      selectLanguage: 'Seleziona Lingua',
      rain: 'Pioggia',
      snow: 'Neve',
      stars: 'Stelle'
    },
    music: {
      fire: 'Fuoco',
      forest: 'Foresta',
      water: 'Acqua',
      ocean: 'Suoni dell\'Oceano',
      faraway: 'Lontano',
      cafe: 'Caffè',
      storm: 'Tempesta',
      woodenFish: 'Pesce di Legno',
      naturalSounds: 'Suoni Naturali',
      ambientMusic: 'Musica Ambientale',
      lightMusic: 'Musica Leggera',
      meditationMusic: 'Musica di Meditazione',
      unknown: 'Sconosciuto'
    },
    settings: {
      title: 'Impostazioni',
      languageSettings: 'Impostazioni Lingua',
      displaySettings: 'Impostazioni Display',
      musicSettings: 'Impostazioni Musica',
      hideBackground: 'Nascondi Immagine di Sfondo',
      volumeControl: 'Controllo Volume',
      uploadMusic: 'Carica Musica',
      uploadedMusic: 'Musica Caricata',
      setVolumeTo: 'Imposta volume a',
      delete: 'Elimina',
      low: 'Basso',
      medium: 'Medio',
      high: 'Alto',
      selectAudioFile: 'Si prega di selezionare un file audio',
      userUploaded: 'Caricato dall\'utente'
    },
    languages: {
      'zh-CN': 'Cinese',
      'en-US': 'Inglese',
      'ja-JP': 'Giapponese',
      'es-ES': 'Spagnolo',
      'fr-FR': 'Francese',
      'de-DE': 'Tedesco',
      'ko-KR': 'Coreano',
      'pt-BR': 'Portoghese',
      'ru-RU': 'Russo',
      'it-IT': 'Italiano'
    }
  }
}

export function useI18n() {
  const t = (key) => {
    const keys = key.split('.')
    let value = messages[currentLanguage.value]
    
    for (const k of keys) {
      value = value?.[k]
    }
    
    return value || key
  }
  
  const setLanguage = (lang) => {
    if (messages[lang]) {
      currentLanguage.value = lang
    }
  }
  
  const availableLanguages = computed(() => Object.keys(messages))
  
  return {
    t,
    currentLanguage: computed(() => currentLanguage.value),
    setLanguage,
    availableLanguages
  }
}